import { ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { SidePanelLayerType } from "@nova/constants"
import { Input } from "@nova/ds-component-input"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { sidePanelLayerTypeByProtobufEntityType, EntityType } from "@nova/util-side-panel"
import classNames from "classnames"
import {
    selectActiveWorkspace,
    selectLegacyWorkspaceTree,
    selectActiveWorkspaceId,
    WorkspaceTreeItem,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectAllFires, selectProjectsByTagFilter } from "features/entity-caching/fires/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useState, useMemo, useEffect, ReactNode, useCallback } from "react"
import * as Icons from "@nova/component-icons"
import { getSidePanelLayerTypeToPrimaryIconMap } from "plugins/plugin-registry"
import { useAddLinkedLayers, postLinkedLayers } from "./LinkedLayerQueries"
import { useParams } from "react-router"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { ProjectExtended } from "@nova/schemas"
import { SearchTag } from "features/data-management/workspace/workspace/body/navigation-bar/search-bar/SearchTag"
import { pluralize } from "@nova/util-strings"

export const useHandleLinkLayersBulkSubmission = (missionIds: string[]) => {
    const dispatch = useAppDispatch()
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const handleLinkLayers = useCallback(
        async ({ projectId, layerIds }: { projectId: string; layerIds: string[] }) => {
            if (!projectId || !layerIds.length || !orgId) return
            try {
                await Promise.all(
                    missionIds.map((missionId) =>
                        postLinkedLayers(orgId, missionId, { projectId, layerIds })
                    )
                )
                dispatch(
                    setGlobalNotificationContext({
                        message: `${pluralize({ count: layerIds.length, str: "layer" })} linked to ${pluralize({ count: missionIds.length, str: "flight" })}.`,
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 3000,
                    })
                )
            } catch {
                dispatch(
                    setGlobalNotificationContext({
                        message: `Failed to link layers. Please try again.`,
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )
            }
        },
        [missionIds, orgId, dispatch]
    )
    return handleLinkLayers
}

const ALLOWED_LAYER_TYPES = new Set<SidePanelLayerType>([
    SidePanelLayerType.OrthomosaicFlight,
    SidePanelLayerType.HotspotFlight,
    SidePanelLayerType.FirePerimeter,
    SidePanelLayerType.VideoColorFinder,
    SidePanelLayerType.ImageColorFinder,
    SidePanelLayerType.LiveVideoFlight,
    SidePanelLayerType.VideoFlight,
])

function folderHasProjectDescendant(
    folder: WorkspaceTreeItem,
    allProjects: Record<string, ProjectExtended | undefined>
): boolean {
    const folderIds = new Set<string>()
    const collectIds = (item: WorkspaceTreeItem) => {
        folderIds.add(item.id)
        for (const sub of item.subTags ?? []) collectIds(sub)
    }
    collectIds(folder)
    return Object.values(allProjects).some((p) => p?.tagId && folderIds.has(p.tagId))
}

function getProjectFolderPath(
    tagId: string | undefined,
    tree: WorkspaceTreeItem[],
    workspaceName: string
): string {
    const findPath = (
        items: WorkspaceTreeItem[],
        target: string,
        path: string[]
    ): string[] | null => {
        for (const item of items) {
            const next = [...path, item.name]
            if (item.id === target) return next
            const found = findPath(item.subTags ?? [], target, next)
            if (found) return found
        }
        return null
    }
    if (!tagId) return ""
    const segments = findPath(tree, tagId, [])
    if (!segments) return ""
    return [workspaceName, ...segments].join(" / ")
}

export const useHandleLinkLayersSubmission = () => {
    const dispatch = useAppDispatch()
    const { missionId = "" } = useParams()
    const { addLinkedLayers } = useAddLinkedLayers(missionId ?? "")
    const handleLinkLayers = useCallback(
        async ({ projectId, layerIds }: { projectId: string; layerIds: string[] }) => {
            if (!projectId || !layerIds.length) return
            try {
                await addLinkedLayers({ projectId, layerIds })
                dispatch(
                    setGlobalNotificationContext({
                        message: `${pluralize({ count: layerIds.length, single: "layer has", plural: "layers have" })} been linked.`,
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 3000,
                    })
                )
            } catch {
                dispatch(
                    setGlobalNotificationContext({
                        message: `Failed to link ${pluralize({ count: layerIds.length, str: "layer" })}. Please try again.`,
                        status: GlobalNotificationStatus.error,
                        timeoutInMs: 5000,
                    })
                )
            }
        },
        [addLinkedLayers, dispatch]
    )
    return handleLinkLayers
}

export const LinkLayersModal = ({
    confirm,
    cancel,
    isPerformingAsyncConfirmCallback,
}: {
    confirm: (args: { projectId: string; layerIds: string[] }) => void
    cancel: () => void
    isPerformingAsyncConfirmCallback: boolean
}) => {
    const [folderStack, setFolderStack] = useState<WorkspaceTreeItem[]>([])
    const [selectedProject, setSelectedProject] = useState<ProjectExtended | null>(null)
    const [layers, setLayers] = useState<{ id: string; name: string; type: string }[] | null>(null)
    const [isLoadingLayers, setIsLoadingLayers] = useState(false)
    const [selectedLayerIds, setSelectedLayerIds] = useState<Set<string>>(new Set())
    const [layerSearchQuery, setLayerSearchQuery] = useState("")
    const [projectSearchQuery, setProjectSearchQuery] = useState("")

    const workspace = useAppSelector(selectActiveWorkspace())
    const workspaceTree = useAppSelector(selectLegacyWorkspaceTree) ?? []
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const allProjects = useAppSelector(selectAllFires)
    const tagIdChain = useMemo(() => folderStack.map((f) => f.id), [folderStack])
    const projects = useAppSelector(selectProjectsByTagFilter(tagIdChain))

    const currentFolders = (
        folderStack.length > 0 ? (folderStack.at(-1)?.subTags ?? []) : workspaceTree
    ).filter((folder) => folderHasProjectDescendant(folder, allProjects))

    useEffect(() => {
        if (!selectedProject || !orgId) {
            setLayers(null)
            return
        }
        let cancelled = false
        setIsLoadingLayers(true)
        fetchApiGateway("get /org/:orgId/project/:projectId/layers", {
            params: { orgId, projectId: selectedProject.id },
        })
            .then((data) => {
                if (!cancelled) setLayers(data)
            })
            .catch(() => {
                if (!cancelled) setLayers([])
            })
            .finally(() => {
                if (!cancelled) setIsLoadingLayers(false)
            })
        return () => {
            cancelled = true
        }
    }, [selectedProject, orgId])

    const filteredLayers = useMemo(() => {
        if (!layers) return []
        const allowed = layers.filter((l) =>
            ALLOWED_LAYER_TYPES.has(sidePanelLayerTypeByProtobufEntityType[l.type as EntityType])
        )
        if (!layerSearchQuery.trim()) return allowed
        const q = layerSearchQuery.toLowerCase()
        return allowed.filter((l) => l.name.toLowerCase().includes(q))
    }, [layers, layerSearchQuery])

    const subtreeTagIds = useMemo(() => {
        if (folderStack.length === 0) return null
        const ids = new Set<string>()
        const collect = (item: WorkspaceTreeItem) => {
            ids.add(item.id)
            for (const sub of item.subTags ?? []) collect(sub)
        }
        collect(folderStack.at(-1)!)
        return ids
    }, [folderStack])

    const filteredProjectSearchResults = useMemo(() => {
        if (!projectSearchQuery.trim()) return []
        const q = projectSearchQuery.toLowerCase()
        return Object.values(allProjects).filter((p): p is ProjectExtended => {
            if (!String(p?.name).toLowerCase().includes(q)) return false
            if (subtreeTagIds === null) return true
            return subtreeTagIds.has(p?.tagId as string)
        })
    }, [projectSearchQuery, allProjects, subtreeTagIds])

    const toggleLayer = (id: string) => {
        setSelectedLayerIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    const workspaceName = workspace?.name ?? "Workspace"

    const breadcrumb = selectedProject
        ? [...folderStack.map((f) => f.name), String(selectedProject.name)]
        : folderStack.map((f) => f.name)

    const handleBack = () => {
        if (selectedProject) {
            setSelectedProject(null)
            setLayers(null)
            setLayerSearchQuery("")
            setSelectedLayerIds(new Set())
        } else {
            setFolderStack((s) => s.slice(0, -1))
        }
    }

    const showBack = folderStack.length > 0 || selectedProject !== null
    const linkCount = selectedLayerIds.size

    return (
        <HighPriorityNovaModalContainer
            title="Link Layers"
            className="w-full max-w-[500px]"
            closeModal={cancel}
            bottomBar={
                <div className="flex gap-2 justify-end w-full">
                    <ButtonTertiary title="Cancel" onClick={cancel} />
                    <ButtonPrimary
                        title={
                            linkCount > 0
                                ? `Link ${linkCount} Layer${linkCount > 1 ? "s" : ""}`
                                : "Link Layers"
                        }
                        disabled={linkCount === 0 || isPerformingAsyncConfirmCallback}
                        onClick={() =>
                            confirm({
                                projectId: selectedProject?.id || "",
                                layerIds: Array.from(selectedLayerIds),
                            })
                        }
                    />
                </div>
            }
        >
            <div className="flex flex-col gap-3 mt-2 overflow-hidden">
                <div className="p-0.5">
                    {selectedProject ? (
                        <Input
                            value={layerSearchQuery}
                            onChange={(e) => setLayerSearchQuery(e.currentTarget.value)}
                            placeholder="Search for layers"
                            leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />}
                            className="!rounded-full focus:!bg-gray-500 focus-within:!bg-gray-500"
                            size="sm"
                        />
                    ) : (
                        <Input
                            value={projectSearchQuery}
                            onChange={(e) => setProjectSearchQuery(e.currentTarget.value)}
                            placeholder="Search for projects"
                            leftItem={
                                <div className="flex items-center gap-2">
                                    <Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />
                                    {folderStack.length > 0 && !!projectSearchQuery && (
                                        <SearchTag
                                            title={folderStack.at(-1)?.name || ""}
                                            onClick={() => setFolderStack([])}
                                        />
                                    )}
                                </div>
                            }
                            className="!rounded-full focus:!bg-gray-500 focus-within:!bg-gray-500"
                            size="sm"
                        />
                    )}
                </div>

                <div className="flex justify-between">
                    <div className="text-gray-100">Link to</div>
                    {selectedLayerIds.size > 0 && (
                        <div
                            className="shrink-0 text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
                            onClick={() => setSelectedLayerIds(new Set())}
                        >
                            Clear selection
                        </div>
                    )}
                </div>

                <div className="border border-gray-700 rounded-md overflow-hidden">
                    <div className="flex items-center gap-1 text-sm text-gray-400 min-w-0 px-6 py-2">
                        {projectSearchQuery.trim() ? (
                            <span className="text-gray-400">Searching</span>
                        ) : (
                            <>
                                {showBack && (
                                    <button
                                        className="shrink-0 flex items-center hover:text-white transition-colors mr-1"
                                        onClick={handleBack}
                                    >
                                        <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                                    </button>
                                )}
                                <span className="shrink-0 text-gray-400">{workspaceName}</span>
                                {breadcrumb.map((segment, i) => (
                                    <span key={i} className="flex items-center gap-1 shrink-0">
                                        <span className="text-gray-600">/</span>
                                        <span
                                            className={classNames({
                                                "text-white font-medium":
                                                    i === breadcrumb.length - 1,
                                                "text-gray-400": i !== breadcrumb.length - 1,
                                            })}
                                        >
                                            {segment}
                                        </span>
                                    </span>
                                ))}
                            </>
                        )}
                    </div>

                    <div className="w-full h-px bg-gray-700" />

                    <ScrollableArea className="h-full max-h-wallboard-content">
                        <div className="flex flex-col gap-0.5 overflow-hidden p-0.5">
                            {selectedProject ? (
                                isLoadingLayers ? (
                                    <div className="px-4 py-6 text-sm text-gray-400 text-center">
                                        Loading layers...
                                    </div>
                                ) : filteredLayers.length === 0 ? (
                                    <div className="px-4 py-8 flex flex-col items-center gap-1 text-center">
                                        <p className="text-white font-medium">No Results Found</p>
                                        <p className="text-gray-400 text-sm">
                                            Try a different keyword, or search by layer name.
                                        </p>
                                    </div>
                                ) : (
                                    filteredLayers.map((layer) => {
                                        const selected = selectedLayerIds.has(layer.id)
                                        return (
                                            <LayerItem
                                                key={layer.id}
                                                title={layer.name}
                                                selected={selected}
                                                toggle={() => toggleLayer(layer.id)}
                                                leftItem={
                                                    <Icons.SmallIcon
                                                        Icon={
                                                            getSidePanelLayerTypeToPrimaryIconMap()[
                                                                sidePanelLayerTypeByProtobufEntityType[
                                                                    layer.type as EntityType
                                                                ]
                                                            ]
                                                        }
                                                        className={classNames(
                                                            "shrink-0 group-hover:text-gray-100 transition-colors",
                                                            {
                                                                "text-gray-100": selected,
                                                                "text-gray-400": !selected,
                                                            }
                                                        )}
                                                    />
                                                }
                                            />
                                        )
                                    })
                                )
                            ) : projectSearchQuery.trim() ? (
                                filteredProjectSearchResults.length === 0 ? (
                                    <div className="px-4 py-8 flex flex-col items-center gap-1 text-center">
                                        <p className="text-white font-medium">No Results Found</p>
                                        <p className="text-gray-400 text-sm">
                                            Try a different keyword, or search by project name.
                                        </p>
                                    </div>
                                ) : (
                                    filteredProjectSearchResults.map((project) => (
                                        <ProjectOrFolderItem
                                            key={project.id}
                                            title={String(project.name)}
                                            folderPath={getProjectFolderPath(
                                                project.tagId as string | undefined,
                                                workspaceTree,
                                                workspaceName
                                            )}
                                            leftItem={
                                                <div className="w-5 min-w-[20px] aspect-square flex justify-center items-center">
                                                    <div className="h-full w-1 rounded-full bg-gray-400 group-hover:bg-white transition-colors rotate-45" />
                                                </div>
                                            }
                                            select={() => {
                                                setSelectedProject(project)
                                                setProjectSearchQuery("")
                                            }}
                                        />
                                    ))
                                )
                            ) : currentFolders.length === 0 && projects.length === 0 ? (
                                <div className="px-4 py-8 flex flex-col items-center gap-1 text-center">
                                    <p className="text-white font-medium">This folder is empty</p>
                                </div>
                            ) : (
                                <>
                                    {currentFolders.map((folder) => (
                                        <ProjectOrFolderItem
                                            key={folder.id}
                                            title={folder.name}
                                            leftItem={
                                                <Icons.SmallIcon
                                                    Icon={Icons.Folder}
                                                    className="shrink-0 text-gray-400 group-hover:text-white transition-colors"
                                                />
                                            }
                                            select={() => setFolderStack((s) => [...s, folder])}
                                        />
                                    ))}
                                    {projects.map((project) => (
                                        <ProjectOrFolderItem
                                            key={project.id}
                                            title={`${project.name}`}
                                            leftItem={
                                                <div className="w-5 min-w-[20px] aspect-square flex justify-center items-center">
                                                    <div className="h-full w-1 rounded-full bg-gray-400 group-hover:bg-white transition-colors rotate-45" />
                                                </div>
                                            }
                                            select={() => setSelectedProject(project)}
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    </ScrollableArea>
                </div>
            </div>
        </HighPriorityNovaModalContainer>
    )
}

const LayerItem = ({
    selected,
    toggle,
    leftItem,
    title,
}: {
    selected: boolean
    toggle: () => void
    leftItem: ReactNode
    title: string
}) => {
    return (
        <div
            className={classNames(
                "group flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer hover:bg-gray-700 rounded-lg",
                {
                    "bg-gray-700": selected,
                }
            )}
            onClick={toggle}
        >
            {leftItem}
            <div className="flex-1 text-sm text-white">{title}</div>
            <TriCheckbox state={selected ? "checked" : "unchecked"} />
        </div>
    )
}

const ProjectOrFolderItem = ({
    select,
    leftItem,
    title,
    folderPath,
}: {
    select: () => void
    leftItem: ReactNode
    title: string
    folderPath?: string
}) => {
    return (
        <div
            className="group flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-700 transition-colors cursor-pointer rounded-lg"
            onClick={select}
        >
            {leftItem}
            <div className="grow flex flex-col">
                {folderPath && <small className="text-gray-300">{folderPath}</small>}
                <span className="flex-1 text-sm text-white">{title}</span>
            </div>
            <Icons.SmallIcon
                Icon={Icons.ChevronRight}
                className="shrink-0 text-gray-500 group-hover:text-gray-100 transition-colors"
            />
        </div>
    )
}
